import React from "react";
import { useResponsive } from "@outplayed/responsive";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import TrinketsContainer from "./TrinketsContainer";
import { useWowPath, getEncounterId } from "../../../hooks/path-hooks";
import {
  getAbbreviatedClass,
  getAbbreviatedSpec,
  getDifficultyPath,
  getFormattedContentType,
  getFormattedNames,
  getIsHealer,
  getIsTank,
  PATCH,
  getFormattedEncounterType,
  getIcyVeinsLinkName,
  getIcyVeinsRole,
  EXPANSION,
} from "../../../lib/wowHelpers";
import { useQueryString } from "../../../hooks/general-hooks";
import { API_WOW_ROOT_PATH } from "../../../api/apiHelpers";
import SubtitleAndDescription from "../../Common/SubtitleAndDescription";
import NoData from "components/Common/NoData";
import Layout from "../Layout";
import PageSectionCrossLinks from "../../CrossLinks/PageSectionCrossLinks";
import { SPECS_TO_HERO_TREES } from "lib/classes-and-specs";

const TrinketsPageContainer = () => {
  const { currentBreakpoint } = useResponsive();
  const isMobile =
    currentBreakpoint === "MOBILE_SMALL" || currentBreakpoint === "MOBILE_MEDIUM" || currentBreakpoint === "MOBILE_LARGE";

  const { classType, specType, encounterType, optionalParams } = useWowPath();
  const encounterID = getEncounterId(optionalParams, encounterType);
  const { displayClassName, displaySpecName, dataClassName, dataSpecName } = getFormattedNames(classType, specType);

  const { difficulty, affix } = useQueryString();
  const diffPath = getDifficultyPath(difficulty, encounterType);
  const { data: trinketsData, loading: isLoading } = useJSONFetcher(
    `${API_WOW_ROOT_PATH}/${diffPath}/${dataClassName}/${dataClassName}_trinkets.json`,
    { ssr: true },
  );

  if (isLoading) {
    return null;
  }

  const dataClassSpec = `${dataClassName}-${dataSpecName}`;
  const hasData =
    trinketsData &&
    (affix && affix !== "all"
      ? (trinketsData.affixes?.[affix]?.[encounterID]?.[dataClassSpec]?.trinkets?.total ?? 0) > 0
      : (trinketsData[encounterType]?.[encounterID]?.[dataClassSpec]?.trinkets?.total ?? 0) > 0);

  const encounterData =
    hasData &&
    (affix && affix !== "all"
      ? trinketsData.affixes[affix][encounterID][dataClassSpec].trinkets
      : trinketsData[encounterType][encounterID][dataClassSpec].trinkets);
  const trinkets = encounterData?.trinket_combos ?? [];
  const trinketPairs = encounterData?.trinket_combos2 ?? [];
  const totalTrinkets = encounterData?.total_trinkets ?? [];

  const isHealer = getIsHealer(displaySpecName);
  const isTank = getIsTank(displaySpecName);

  //BIS trinkets guide for [spec-class1]. Get the best [hero-spec-1] and [hero-spec-2] [spec-class2] trinkets, data-driven by top players in [endgame3], for The War Within [patch].
  const pageDescription = `BIS trinkets guide for ${displaySpecName} ${displayClassName}. Get the best ${
    SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][0]
  } and ${SPECS_TO_HERO_TREES[displayClassName].specs[displaySpecName][1]} ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(displayClassName)} trinkets, data-driven by top players in ${getFormattedEncounterType(
    encounterType,
  )}, for ${EXPANSION} ${PATCH}.`;

  const metaDescription = `BIS trinkets guide for ${displaySpecName} ${displayClassName}. Get the best ${getAbbreviatedSpec(
    displaySpecName,
  )} ${getAbbreviatedClass(displayClassName)} trinkets, data-driven by top players in ${getFormattedEncounterType(
    encounterType,
  )}, for ${EXPANSION} ${PATCH}.`;

  return (
    <Layout
      className="wow-trinkets-container"
      classType={classType}
      filterPage="builds"
      metaDescription={metaDescription}
      metaTitle={`Trinkets for ${getFormattedEncounterType(encounterType)}`}
      pageDescription={pageDescription}
      pageTitle={`Trinkets for ${getFormattedEncounterType(encounterType)}`}
      specType={specType}
    >
      {hasData ? (
        <>
          <SubtitleAndDescription
            subtitle={`The Best ${getAbbreviatedSpec(displaySpecName)} ${getAbbreviatedClass(
              displayClassName,
            )} Trinkets for ${getFormattedContentType(encounterType)}`}
            description={pageDescription}
          />
          <TrinketsContainer
            trinkets={trinkets}
            trinketPairs={trinketPairs}
            totalTrinkets={totalTrinkets}
            isMobile={isMobile}
            isHealer={isHealer}
            isTank={isTank}
            encounterType={encounterType}
          />
          <PageSectionCrossLinks
            classType={classType}
            description={`Our friends at Icy Veins have you covered, with an in-depth guide to suit up your ${displaySpecName} ${displayClassName} with the best trinkets and other gear.`}
            guideData={[
              {
                description: `The best in-depth guides covering trinkets and gear for ${displaySpecName} ${displayClassName}`,
                link: `https://www.icy-veins.com/wow/${getIcyVeinsLinkName(specType)}-${getIcyVeinsLinkName(
                  classType,
                )}-pve-${getIcyVeinsRole(specType)}-gear-best-in-slot`,
                title: "Gear and Best in Slot Guide",
              },
            ]}
            specType={specType}
          />
        </>
      ) : (
        <NoData specType={specType} />
      )}
    </Layout>
  );
};

export default TrinketsPageContainer;
